import { call, select } from 'redux-saga/effects';
import {
  getToken,
  setToken,
  airbrakeProject,
  decodeToken,
  setSolargrafToken
} from '../utils/helper';
import { assign, isEmpty } from '../utils/lodash';
import settings from '../utils/settings';
import I18n from '../config/locales';
import { Notifier } from '@airbrake/browser';
import getConfig from '../config/env';
import { get as getValue } from './lodash';

const getTokenFromReduxStore = (state) =>
  state.appReducer.enlightenManagerToken;

const COMMON_API_ERROR_MESSAGE = I18n.t('utils.commonApiMessage');
const FAILED_TO_FETCH_ERROR_MESSAGE = 'Failed to fetch';

function ApiError(message) {
  this.message = message || '';
}
ApiError.prototype = new Error();

const DEFAULT_API_OPTIONS = {
  method: 'GET',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
};

/**
 * Requests a URL, returning a promise
 * @param  {object} options The options we want to pass to "fetch"
 * @return {object} The response data
 */
function request(options) {
  return fetch(options.url, options)
    .then((response) => {
      const statusCode = response.status;

      return response.json().then((jsonResult) => {
        const result = {
          success: true,
          status: statusCode,
          result: jsonResult,
          statusCode
        };
        if (response?.headers?.get('x-csrf-token')) {
          result.XSRFTOKEN = response?.headers?.get('x-csrf-token');
        }
        const { invalid_session = false } = jsonResult;
        if (statusCode === 401) {
          if (invalid_session) {
            setToken(-1);
            setSolargrafToken('');
            const { enlightenManagerToken } = options;
            let jwtToken = getToken();
            if (!jwtToken || jwtToken === '' || jwtToken === -1)
              jwtToken = enlightenManagerToken;
            const { login_user = '' } = decodeToken(jwtToken);
            if (isEmpty(login_user)) {
              window.location = settings.enlightenMenuUrl('logout');
            } else {
              window.location = settings.enlightenMenuUrl('logout_as');
            }
            result.result.message = I18n.t('utils.signInMessage');
            result.isUnauthorized = true;
            result.success = false;
          } else if (
            /\/authorized_dealers\//.test(options.url) &&
            !/\/manager\/(error|dashboard)$/.test(document.location.pathname)
          ) {
            window.location = settings.enlightenMenuUrl('');
          }
          result.result.message = I18n.t('utils.signInMessage');
          result.isUnauthorized = true;
          result.success = false;
        }
        if (response.ok) return result;

        result.result.message =
          jsonResult.message ||
          jsonResult.exception ||
          jsonResult.info ||
          COMMON_API_ERROR_MESSAGE;
        result.success = false;
        return result;
      });
    })
    .catch((error) => {
      const defaultErrorMessage = error.message || COMMON_API_ERROR_MESSAGE;
      return {
        success: false,
        status: 'Internal Error',
        statusCode: 500,
        error: error,
        result: {
          message: defaultErrorMessage
        }
      };
    });
}

const makeOptions = (options) => {
  let url = options.url;
  const {
    withBearer = false,
    enlightenManagerToken,
    externalServiceToken = '',
    XSRFTOKEN = ''
  } = options;
  let jwtToken = externalServiceToken ? externalServiceToken : getToken();
  if (
    !externalServiceToken &&
    (!jwtToken || jwtToken === '' || jwtToken === -1)
  )
    jwtToken = enlightenManagerToken;
  let token = withBearer ? `Bearer ${jwtToken}` : jwtToken;
  DEFAULT_API_OPTIONS.headers['Authorization'] = token;
  if (XSRFTOKEN) {
    DEFAULT_API_OPTIONS.headers['X-XSRF-TOKEN'] = XSRFTOKEN;
  }
  const body = options.body && JSON.stringify(options.body);
  return assign({}, DEFAULT_API_OPTIONS, options, {
    url,
    body
  });
};

export default function* api(options) {
  const token = yield select(getTokenFromReduxStore);
  options['enlightenManagerToken'] = token;
  const apiOptions = makeOptions(options);
  const response = yield call(request, apiOptions);

  const { success, result, error, statusCode, status } = response;
  if (!success && statusCode != 401) {
    const { airbrakeProjectId, airbrakeProjectKey, companyId } =
      airbrakeProject();
    let airbrake = new Notifier({
      projectId: airbrakeProjectId,
      projectKey: airbrakeProjectKey,
      environment: getConfig()['env']
    });
    // Ignore API Errors from Development and QA4 Env
    airbrake.addFilter((notice) => {
      if (result.message.includes(FAILED_TO_FETCH_ERROR_MESSAGE)) return null;

      if (notice.context.environment === 'qa2') {
        return notice;
      } else if (
        notice.context.environment === 'production' &&
        Math.floor(Math.random() * 10) <= 0
      ) {
        // Logging only 10% of airbrakes in production
        return notice;
      }
      return null;
    });
    // Send error to Airbrake
    airbrake.notify({
      error: error || 'APIError: ' + result.message,
      params: {
        apiEndpoint: apiOptions.url,
        companyId: companyId,
        statusCode: statusCode,
        status: status
      }
    });
  }
  return response;
}

// const canIgnoreError = notice => {
//   const errors = getValue(notice, 'errors', []);
//   if (errors.length > 0) {
//     const errorMessage = getValue(errors[0], 'message', '');
//     if (
//       errorMessage !== '' &&
//       (errorMessage.includes('You need to sign in or sign up') ||
//         errorMessage.includes('Request could not be processed') ||
//         errorMessage.includes('Unauthorized') ||
//         errorMessage.includes('Not authorized'))
//     )
//       return true;
//   }
//   return false;
// };

export function* get(options) {
  return yield call(api, assign({}, options, { method: 'GET' }));
}

export function* post(options) {
  return yield call(api, assign({}, options, { method: 'POST' }));
}

export function* putMethod(options) {
  return yield call(api, assign({}, options, { method: 'PUT' }));
}

export function* deleteMethod(options) {
  return yield call(api, assign({}, options, { method: 'DELETE' }));
}

export function* patchMethod(options) {
  return yield call(api, assign({}, options, { method: 'PATCH' }));
}
